import React, { useState } from 'react';
import { BarChart3, TrendingUp, PieChart, Download, Users, Target } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const PerformanceAnalytics = ({ employee, performanceHistory, goalHistory, performanceData, goals, employees }) => {
  const [timeRange, setTimeRange] = useState('12_months');
  const [chartType, setChartType] = useState('performance_trends');

  // Sample analytics data - in real app this would come from API
  const analyticsData = {
    performanceTrends: [
      { month: 'Jan', rating: 4.2, goals: 85 },
      { month: 'Feb', rating: 4.3, goals: 88 },
      { month: 'Mar', rating: 4.1, goals: 82 },
      { month: 'Apr', rating: 4.4, goals: 90 },
      { month: 'May', rating: 4.5, goals: 92 },
      { month: 'Jun', rating: 4.3, goals: 87 }
    ],
    ratingDistribution: [
      { rating: '5.0', count: 12, percentage: 15 },
      { rating: '4.0-4.9', count: 45, percentage: 56 },
      { rating: '3.0-3.9', count: 20, percentage: 25 },
      { rating: '2.0-2.9', count: 3, percentage: 4 },
      { rating: '1.0-1.9', count: 0, percentage: 0 }
    ],
    departmentComparison: [
      { department: 'Engineering', avgRating: 4.2, goalCompletion: 85 },
      { department: 'Marketing', avgRating: 4.4, goalCompletion: 90 },
      { department: 'Sales', avgRating: 4.1, goalCompletion: 78 },
      { department: 'HR', avgRating: 4.5, goalCompletion: 95 }
    ]
  };

  const timeRangeOptions = [
    { value: '3_months', label: 'Last 3 Months' },
    { value: '6_months', label: 'Last 6 Months' },
    { value: '12_months', label: 'Last 12 Months' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const chartTypeOptions = [
    { value: 'performance_trends', label: 'Performance Trends' },
    { value: 'goal_completion', label: 'Goal Completion' },
    { value: 'rating_distribution', label: 'Rating Distribution' },
    { value: 'department_comparison', label: 'Department Comparison' }
  ];

  // Calculate insights
  const insights = {
    averageRating: employee 
      ? performanceHistory?.[0]?.currentRating || 0
      : performanceData?.reduce((acc, p) => acc + (p?.currentRating || 0), 0) / (performanceData?.length || 1),
    goalCompletionRate: employee
      ? goalHistory?.filter(g => g?.progress >= 100)?.length / (goalHistory?.length || 1) * 100
      : goals?.filter(g => g?.progress >= 100)?.length / (goals?.length || 1) * 100,
    improvementTrend: '+12%',
    benchmarkPercentile: 75
  };

  const recommendations = [
    {
      type: 'strength',
      title: 'High Goal Completion',
      description: 'Consistently meets and exceeds goal targets. Consider increasing goal complexity.',
      action: 'Set stretch goals'
    },
    {
      type: 'improvement',
      title: 'Communication Skills',
      description: 'Feedback suggests opportunities for improvement in presentation skills.',
      action: 'Enroll in communication training'
    },
    {
      type: 'recognition',
      title: 'Team Collaboration', 
      description: 'Excellent teamwork and peer feedback scores.',
      action: 'Consider for mentorship role'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Analytics Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <BarChart3 className="h-6 w-6 text-blue-600 mr-2" />
            Performance Analytics
            {employee && <span className="text-base font-normal text-gray-600 ml-2">- {employee?.firstName} {employee?.lastName}</span>}
          </h2>
          <div className="flex items-center space-x-3">
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e?.target?.value)}
              className="text-sm"
            >
              {timeRangeOptions?.map(option => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </Select>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {insights?.averageRating?.toFixed(1)}
            </div>
            <div className="text-sm text-blue-600">Average Rating</div>
            <div className="text-xs text-blue-500 mt-1">+0.3 vs last period</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {Math.round(insights?.goalCompletionRate || 0)}%
            </div>
            <div className="text-sm text-green-600">Goal Completion</div>
            <div className="text-xs text-green-500 mt-1">+5% vs last period</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {insights?.improvementTrend}
            </div>
            <div className="text-sm text-purple-600">Improvement Trend</div>
            <div className="text-xs text-purple-500 mt-1">Year over year</div>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-orange-600">
              {insights?.benchmarkPercentile}th
            </div>
            <div className="text-sm text-orange-600">Percentile</div>
            <div className="text-xs text-orange-500 mt-1">Company benchmark</div>
          </div>
        </div>
      </div>

      {/* Chart Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap gap-2">
          {chartTypeOptions?.map(option => (
            <button
              key={option?.value}
              onClick={() => setChartType(option?.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                chartType === option?.value
                  ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {option?.label}
            </button>
          ))}
        </div>
      </div>

      {/* Charts Container */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Trends Chart */}
        {chartType === 'performance_trends' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <TrendingUp className="h-5 w-5 text-blue-600 mr-2" />
              Performance Trends Over Time
            </h3>
            
            {/* Chart Placeholder - In real app, use recharts or similar */}
            <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center mb-4">
              <div className="text-center">
                <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">Performance trend chart would render here</p>
                <p className="text-xs text-gray-400 mt-1">Data: {analyticsData?.performanceTrends?.length} months</p>
              </div>
            </div>

            {/* Data Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Goal Completion</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analyticsData?.performanceTrends?.map((data, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{data?.month}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{data?.rating}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{data?.goals}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Rating Distribution */}
        {chartType === 'rating_distribution' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <PieChart className="h-5 w-5 text-blue-600 mr-2" />
              Rating Distribution
            </h3>
            
            <div className="space-y-3">
              {analyticsData?.ratingDistribution?.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{item?.rating}</span>
                  <div className="flex items-center space-x-3 flex-1 ml-4">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${item?.percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-12">{item?.count}</span>
                    <span className="text-sm text-gray-500 w-12">{item?.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Department Comparison */}
        {chartType === 'department_comparison' && !employee && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-5 w-5 text-blue-600 mr-2" />
              Department Comparison
            </h3>
            
            <div className="space-y-4">
              {analyticsData?.departmentComparison?.map((dept, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">{dept?.department}</h4>
                    <span className="text-sm text-gray-500">{dept?.avgRating} avg</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Rating: </span>
                      <span className="font-medium">{dept?.avgRating}/5</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Goals: </span>
                      <span className="font-medium">{dept?.goalCompletion}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Performance Recommendations */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Target className="h-5 w-5 text-blue-600 mr-2" />
            Recommendations
          </h3>
          
          <div className="space-y-4">
            {recommendations?.map((rec, index) => (
              <div key={index} className={`border rounded-lg p-4 ${
                rec?.type === 'strength' ? 'border-green-200 bg-green-50' :
                rec?.type === 'improvement'? 'border-yellow-200 bg-yellow-50' : 'border-blue-200 bg-blue-50'
              }`}>
                <div className="flex items-start space-x-2">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    rec?.type === 'strength' ? 'bg-green-500' :
                    rec?.type === 'improvement'? 'bg-yellow-500' : 'bg-blue-500'
                  }`}></div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-1">{rec?.title}</h4>
                    <p className="text-sm text-gray-600 mb-2">{rec?.description}</p>
                    <Button variant="outline" size="sm">
                      {rec?.action}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Calibration & Benchmarking */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-purple-900 mb-2">Performance Calibration</h3>
        <p className="text-purple-800 text-sm mb-4">
          Ensure consistent rating standards across managers and departments for fair performance evaluation.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-white rounded-lg p-4 border border-purple-100">
            <div className="text-center">
              <div className="text-lg font-bold text-purple-600 mb-1">92%</div>
              <div className="text-sm text-purple-900">Rating Consistency</div>
              <div className="text-xs text-purple-700 mt-1">Across managers</div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-purple-100">
            <div className="text-center">
              <div className="text-lg font-bold text-purple-600 mb-1">4.2</div>
              <div className="text-sm text-purple-900">Company Average</div>
              <div className="text-xs text-purple-700 mt-1">All employees</div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-purple-100">
            <div className="text-center">
              <div className="text-lg font-bold text-purple-600 mb-1">85%</div>
              <div className="text-sm text-purple-900">Goal Alignment</div>
              <div className="text-xs text-purple-700 mt-1">With company objectives</div>
            </div>
          </div>
        </div>
        <div className="flex justify-center mt-4">
          <Button variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-100">
            View Calibration Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PerformanceAnalytics;